import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Collapse,
  Alert,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionIcon from '@mui/icons-material/Description';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

function ClientListItem({ client, onClick, showDetail }) {
  return (
    <ListItem disablePadding sx={{ mb: 0.25 }}>
      <ListItemButton
        onClick={() => onClick(client.link)}
        sx={{ py: 0.25, px: 1, borderRadius: 1 }}
      >
        <ListItemIcon sx={{ minWidth: 20 }}>
          <ChevronRightIcon fontSize="small" color="action" />
        </ListItemIcon>
        <ListItemText
          primary={
            showDetail ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="primary" component="span">
                  {client.initials}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="span">
                  - {showDetail(client)}
                </Typography>
              </Box>
            ) : (
              client.initials
            )
          }
          primaryTypographyProps={showDetail ? undefined : { variant: 'body2', color: 'primary' }}
        />
      </ListItemButton>
    </ListItem>
  );
}

function ExpandableSnapshotItem({
  icon,
  label,
  expanded,
  onToggle,
  clients,
  onClientClick,
  clientDetailFn = undefined,
}) {
  return (
    <>
      <ListItemButton
        onClick={onToggle}
        sx={{
          py: 0.5,
          px: 0,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 28 }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          primaryTypographyProps={{ variant: 'body2' }}
        />
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List dense disablePadding sx={{ pl: 4 }}>
          {clients?.map((client) => (
            <ClientListItem
              key={client.patient_id}
              client={client}
              onClick={onClientClick}
              showDetail={clientDetailFn}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

function AdminSnapshotSection({ admin_snapshot, expandedSections, toggleSection, handleClientClick }) {
  const errorCount = admin_snapshot?._errors?.length || 0;
  const hasErrors = errorCount > 0;
  const hasItems = admin_snapshot && (
    admin_snapshot.clients_with_billing_issues > 0 ||
    admin_snapshot.clients_with_roi_issues > 0 ||
    admin_snapshot.clients_with_no_followup > 0 ||
    admin_snapshot.clients_with_pending_forms > 0
  );

  if (!hasItems && !hasErrors) return null;

  return (
    <>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Today&apos;s Admin Snapshot
        </Typography>
        <List dense disablePadding>
          {admin_snapshot.clients_with_billing_issues > 0 && (
            <ExpandableSnapshotItem
              icon={<PaymentIcon fontSize="small" color="warning" />}
              label={`${admin_snapshot.clients_with_billing_issues} client${admin_snapshot.clients_with_billing_issues !== 1 ? 's' : ''} today ${admin_snapshot.clients_with_billing_issues !== 1 ? 'have' : 'has'} billing issues`}
              expanded={expandedSections.billing}
              onToggle={() => toggleSection('billing')}
              clients={admin_snapshot.billing_issues_clients}
              onClientClick={handleClientClick}
              clientDetailFn={(client) => client.issue_description || client.status}
            />
          )}

          {admin_snapshot.clients_with_roi_issues > 0 && (
            <ExpandableSnapshotItem
              icon={<DescriptionIcon fontSize="small" color="warning" />}
              label={`${admin_snapshot.clients_with_roi_issues} client${admin_snapshot.clients_with_roi_issues !== 1 ? 's' : ''} today ${admin_snapshot.clients_with_roi_issues !== 1 ? 'have' : 'has'} ROI expiring soon`}
              expanded={expandedSections.roi}
              onToggle={() => toggleSection('roi')}
              clients={admin_snapshot.roi_issues_clients}
              onClientClick={handleClientClick}
            />
          )}

          {admin_snapshot.clients_with_no_followup > 0 && (
            <ExpandableSnapshotItem
              icon={<ScheduleIcon fontSize="small" color="info" />}
              label={`${admin_snapshot.clients_with_no_followup} client${admin_snapshot.clients_with_no_followup !== 1 ? 's' : ''} today ${admin_snapshot.clients_with_no_followup !== 1 ? "don't" : "doesn't"} have a follow-up scheduled`}
              expanded={expandedSections.followup}
              onToggle={() => toggleSection('followup')}
              clients={admin_snapshot.no_followup_clients}
              onClientClick={handleClientClick}
            />
          )}

          {admin_snapshot.clients_with_pending_forms > 0 && (
            <ExpandableSnapshotItem
              icon={<AssignmentIcon fontSize="small" color="info" />}
              label={`${admin_snapshot.clients_with_pending_forms} client${admin_snapshot.clients_with_pending_forms !== 1 ? 's' : ''} today still ${admin_snapshot.clients_with_pending_forms !== 1 ? 'have' : 'has'} pending forms`}
              expanded={expandedSections.forms}
              onToggle={() => toggleSection('forms')}
              clients={admin_snapshot.pending_forms_clients}
              onClientClick={handleClientClick}
              clientDetailFn={(client) =>
                client.form_names?.length > 0
                  ? client.form_names.slice(0, 2).join(', ') + (client.form_names.length > 2 ? ` +${client.form_names.length - 2} more` : '')
                  : `${client.pending_count} form${client.pending_count !== 1 ? 's' : ''}`
              }
            />
          )}
        </List>
        {hasErrors && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorCount === 1
              ? '1 client record could not be loaded. The counts shown may be incomplete.'
              : `${errorCount} client records could not be loaded. The counts shown may be incomplete.`}
          </Alert>
        )}
      </Box>
    </>
  );
}

export default AdminSnapshotSection;
